<template>
  <BittsTooltip placement="right" :mount-to-body="true">
    <component
      :is="isUnsupportedRecord ? 'span' : 'router-link'"
      v-bind="linkBinds"
    >
      <span
        class="c-universal-search-results__item"
        :class="{
          'c-universal-search-results__item--disabled': isUnsupportedRecord,
        }"
        @click="userClickedUniversalSuggestedPartner"
      >
        <BittsAvatar
          v-if="showAvatar"
          :is-icon="!searchResult"
          :org="org"
          :user="user"
          icon-slot-bg-color="bg-beta-background-medium"
          size="medium"
        >
          <template #icon>
            <FontAwesomeIcon
              :icon="['fak', 'company']"
              :style="{ height: '20px', width: '20px', color: 'white' }"
            />
          </template>
        </BittsAvatar>
        <span
          :class="{ 'ml-8': showAvatar }"
          class="c-universal-search-results__item__text"
        >
          <span
            :class="{ 'font-bold': transformedRecord.type !== 'populations' }"
            class="c-universal-search-results__item__title"
          >
            {{ transformedRecord.title }}
          </span>
          <div v-if="showPopulation">
            <div class="flex items-center gap-4 text-sm text-neutral-500">
              <div class="h-16 w-16">
                <BittsSvg :svg="transformedRecord.integration + 'Icon'" />
              </div>
              <div v-if="joinedPopulations.populationString">
                <span>•</span>
                {{ joinedPopulations.populationString }}
              </div>
              <div v-else>
                <span>•</span>
                {{ 'No Populations' }}
              </div>
              <BittsTag
                v-if="joinedPopulations.overflowedPops"
                size="x-small"
                color="neutral"
              >
                {{ joinedPopulations.overflowedPops }}
              </BittsTag>
            </div>
          </div>
          <span v-else class="c-universal-search-results__item__subtitle">
            {{ transformedRecord.subtitle }}
          </span>
        </span>
        <span
          v-if="transformedRecord.hasInviteLink"
          :to="transformedRecord.link"
          class="c-universal-search-results__item__invite-link"
        >
          Click to invite
        </span>
        <span v-if="validPartner" @click.stop>
          <OfflineManagedPartnerLabel
            :partner="validPartner"
            :is-tag="true"
            :mount-to-body="true"
          />
        </span>
      </span>
    </component>
    <template v-if="isUnsupportedRecord" #title>
      Record details are not available for Databricks records yet
    </template>
  </BittsTooltip>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsSvg,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { computed } from 'vue';

import OfflineManagedPartnerLabel from '@/components/partners/OfflineManagedPartnerLabel.vue';

import useIteratively from '@/composables/useIteratively';
import { DATABRICKS_DATA_SOURCE_TYPE } from '@/constants/data_sources';
import { COMPANY, PERSON } from '@/constants/universal_search';
import { usePartnersStore } from '@/stores';
import type { Partner } from '@/types/partners';
import type {
  CompanyRecord,
  PartnerOrg,
  PersonRecord,
  PopulationRecord,
  SearchResult,
  SuggestedPartner,
} from '@/types/search';

type TransformedRecord = {
  type: string;
  subType?: string;
  title: string;
  subtitle?: string;
  domain?: string;
  email?: string;
  logo_url?: string;
  inviteCode?: string;
  hasInviteLink?: boolean;
  link: {
    name: string;
    params?: Record<string, string | number>;
    query?: Record<string, string>;
  };
  integration?: string;
  populations?: string[];
  populationType?: string;
};

const props = defineProps<{
  searchResult?: SearchResult | null;
  searchTerm?: string | null;
}>();

const emit = defineEmits<(e: 'on-search-result-selected') => void>();
const searchResult = computed(() => props.searchResult);
const searchTerm = computed(() => props.searchTerm);

const { iteratively } = useIteratively();

const transformedRecord = computed<TransformedRecord>(() => {
  const transformers = {
    partner_orgs: transformPartnerOrg,
    populations: transformPopulation,
    people: transformPeople,
    companies: transformCompanies,
    suggested: transformSuggested,
    invite: transformInvite,
  };
  // with search redesign, companies and people should both be filed under 'records'
  // subtype is added in fetch in UniversalSearchMenu to keep the original transformation
  if (!searchResult.value) return transformers.invite(searchTerm.value || '');

  const result = searchResult.value;
  const transformerKey = result?.subType || result?.type;
  if (
    transformerKey === 'partner_orgs' &&
    result &&
    'name' in result &&
    'domain' in result
  ) {
    return transformers.partner_orgs(result as PartnerOrg);
  }
  if (
    transformerKey === 'populations' &&
    result &&
    'name' in result &&
    'population_type' in result
  ) {
    return transformers.populations(result as PopulationRecord);
  }
  if (
    transformerKey === 'people' &&
    result &&
    'person_email' in result &&
    'integration' in result
  ) {
    return transformers.people(result as PersonRecord);
  }
  if (
    transformerKey === 'companies' &&
    result &&
    'company_name' in result &&
    'company_domain' in result
  ) {
    return transformers.companies(result as CompanyRecord);
  }
  if (
    transformerKey === 'suggested' &&
    result &&
    'public_invite_code' in result
  ) {
    return transformers.suggested(result as SuggestedPartner);
  }
  if (transformerKey === 'invite') {
    return transformers.invite(searchTerm.value || '');
  }

  return transformers.invite(searchTerm.value || '');
});

const isUnsupportedRecord = computed(
  () => transformedRecord.value.integration === DATABRICKS_DATA_SOURCE_TYPE,
);

const linkBinds = computed(() =>
  isUnsupportedRecord.value
    ? { 'aria-disabled': 'true' }
    : { to: transformedRecord.value.link },
);

const org = computed(() => {
  if (transformedRecord.value?.type === 'partner_orgs')
    return transformedRecord.value;
  if (
    transformedRecord.value?.subType === 'companies' ||
    transformedRecord.value?.type === 'suggested'
  ) {
    return { domain: transformedRecord.value?.domain };
  }
  return null;
});

const user = computed(() => {
  if (transformedRecord.value?.subType === 'people') {
    return { email: transformedRecord.value?.email };
  }
  return null;
});

const showAvatar = computed(
  () => transformedRecord.value?.type !== 'populations',
);

const showPopulation = computed(() => {
  const result = searchResult.value;
  return (
    result?.subType &&
    [COMPANY, PERSON].includes(result.subType) &&
    result?.populations
  );
});

const joinedPopulations = computed(() => {
  /* Returns an object that's helpful for calculating CSS overflow issues
      in the search result. We need to display a tag when the character
      count is over 40. */
  const CHARACTER_LIMIT = 40;
  let remaining = CHARACTER_LIMIT;

  const populations = transformedRecord.value.populations || [];
  const populationString = populations.join(', ');
  if (populationString.length < CHARACTER_LIMIT)
    return { populationString, overflowedPops: 0 };

  const popsToDisplay: string[] = [];
  for (const population of populations) {
    remaining -= population.length + 2;
    if (remaining <= 0 && popsToDisplay.length) break;
    popsToDisplay.push(population);
  }

  return {
    populationString: popsToDisplay.join(', '),
    overflowedPops: populations.length - popsToDisplay.length,
  };
});

function capitalize(str: string | undefined | null): string {
  if (!str || !str.length || typeof str !== 'string') return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}
function transformRecordPopulations(
  record: PersonRecord | CompanyRecord,
): string[] {
  if (!record.populations) return [];

  const nameComparer = (a: { name: string }, b: { name: string }) => {
    if (a.name > b.name) return 1;
    if (a.name < b.name) return -1;
    return 0;
  };
  const standardPopulations = record.populations
    .filter((pop) => pop && pop.standard_type)
    .sort(nameComparer);
  const customPopulations = record.populations
    .filter((pop) => pop && !pop.standard_type)
    .sort(nameComparer);
  return [...standardPopulations, ...customPopulations]
    .map((pop) => pop?.name)
    .filter(Boolean);
}
function userClickedUniversalSuggestedPartner() {
  if (isUnsupportedRecord.value) return;
  emit('on-search-result-selected');
  if (transformedRecord.value.type === 'suggested') {
    iteratively.userClickedUniversalSuggestedPartner({
      partner_url: transformedRecord.value.domain?.toString() || '',
      event_site: EVENT_SITES.UNIVERSAL_SEARCH_RESULT,
    });
  }
}
function transformSuggested(record: SuggestedPartner) {
  return {
    type: record.type,
    title: record.name,
    domain: record.domain,
    inviteCode: record.public_invite_code,
    hasInviteLink: true,
    link: {
      name: 'create_proposal',
      query: {
        invite: record.public_invite_code,
        name: record.name,
        domain: record.domain,
        universal_suggested: 'true',
      },
    },
  };
}
function transformPartnerOrg(record: PartnerOrg) {
  return {
    type: record.type,
    title: record.name,
    subtitle: record.domain ? `${record.domain}` : '',
    domain: record.domain,
    logo_url: record.logo_url,
    link: {
      name: 'partner_details',
      params: {
        partner_org_id: record.id,
      },
    },
  };
}

const partnersStore = usePartnersStore();
const partner = computed(() => {
  const link = transformedRecord.value?.link;
  const params = link?.params;
  const partnerOrgId = params?.partner_org_id;

  if (!partnerOrgId) return false;

  return partnersStore.getPartnerOrgById(Number(partnerOrgId));
});

const isOfflinePartner = computed(() => {
  if (!partner.value) return false;
  return partner.value?.offline_partner && !partner.value?.virtual_partner;
});

const isManagedPartner = computed(() => {
  if (!partner.value) return false;
  return partner.value?.virtual_partner;
});

const validPartner = computed((): Partner | null => {
  if (!partner.value || typeof partner.value === 'boolean') return null;
  if (isOfflinePartner.value || isManagedPartner.value) {
    return partner.value as Partner;
  }
  return null;
});

function transformPopulation(record: PopulationRecord) {
  return {
    type: record.type,
    title: capitalize(record.name),
    populationType: record.population_type,
    link: {
      name: 'edit_population',
      params: {
        population_id: record.id,
      },
    },
  };
}
function transformPeople(record: PersonRecord) {
  return {
    type: record.type,
    subType: record.subType,
    email: record.person_email,
    title: record.person_email,
    subtitle: `${capitalize(
      typeof record.integration === 'string'
        ? record.integration
        : (record.integration as { friendly_name: string }).friendly_name,
    )} | ${capitalize(record.source_name)}`,
    link: {
      name: 'individual_record',
      params: {
        source_id: record.source_id,
        source_primary_key: record.source_primary_key,
      },
    },
    integration:
      typeof record.integration === 'string'
        ? record.integration
        : (record.integration as { type: string }).type,
    populations: transformRecordPopulations(record),
  };
}
function transformCompanies(record: CompanyRecord) {
  return {
    type: record.type,
    subType: record.subType,
    domain: record.company_domain,
    title: record.company_name,
    subtitle: `${capitalize(
      typeof record.integration === 'string'
        ? record.integration
        : (record.integration as { friendly_name: string }).friendly_name,
    )} | ${capitalize(record.source_name)}`,
    link: {
      name: 'individual_record',
      params: {
        source_id: record.source_id,
        source_primary_key: record.source_primary_key,
      },
    },
    integration:
      typeof record.integration === 'string'
        ? record.integration
        : (record.integration as { type: string }).type,
    populations: transformRecordPopulations(record),
  };
}
function transformInvite(searchTerm: string) {
  return {
    type: 'invite',
    title: `Invite "${searchTerm}" to Crossbeam`,
    subTitle: '',
    domain: '',
    link: {
      name: 'create_proposal',
      query: {
        name: searchTerm,
        universal_suggested: 'true',
      },
    },
  };
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.c-universal-search-results__item {
  @apply mr-4 flex justify-between items-center py-4 px-8 text-base relative;
  white-space: nowrap;
  text-decoration: none;
  &:hover {
    @apply bg-neutral-background-weak rounded-6;
  }
}

.c-universal-search-results__item--disabled {
  @apply cursor-not-allowed opacity-50;
  &:hover {
    @apply bg-transparent;
  }
}

.c-universal-search-results__item__text {
  @apply text-brand-navy;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  overflow: hidden;
  text-overflow: ellipsis;
}

.c-universal-search-results__item__title {
  overflow: hidden;
  text-overflow: ellipsis;
  @apply text-neutral-900 leading-6;
}

.c-universal-search-results__item__subtitle {
  overflow: hidden;
  text-overflow: ellipsis;
  @apply text-sm text-neutral-500;
}
</style>
